import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import FormButton from "../../../components/form/FormButton";

import {
  Ban,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  List,
  Loader2,
  Search,
  UserRound,
  X,
} from "lucide-react";

import {
  blockAvailability,
  createRecurringUnblockException,
  getAvailabilityBoard,
  unblockAvailability,
} from "./availabilityApi";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import "./availability-board.css";

const ymd = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const fromYmd = (value) => {
  const [year, month, day] = String(value).split("-").map(Number);
  return new Date(year, month - 1, day);
};

const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const formatDay = (date, options) =>
  new Intl.DateTimeFormat("en-US", options).format(date);

const formatTime = (value) => {
  const [hours, minutes = "00"] = String(value || "00:00").split(":");
  const hour = Number(hours);
  const suffix = hour >= 12 ? "pm" : "am";
  return `${hour % 12 || 12}${minutes === "00" ? "" : `:${minutes}`}${suffix}`;
};

const slotLabel = (slot) =>
  `${formatTime(slot.startTime)}–${formatTime(slot.endTime)}`;

const STATUS_COPY = {
  available: "Available — tap to block",
  blocked: "Blocked by venue",
  booked: "Booked — view booking information",
  hold: "Payment hold — view information",
};

const LAST_VENUE_STORAGE_KEY = "og-availability-last-venue-id";

const getStoredVenueId = () => {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(LAST_VENUE_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

const setStoredVenueId = (id) => {
  if (typeof window === "undefined") return;
  try {
    if (id) window.localStorage.setItem(LAST_VENUE_STORAGE_KEY, id);
  } catch {
  }
};

const SearchableCombobox = ({
  id,
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled = false,
  emptyMessage = "No options",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const touchYRef = useRef(null);

  const selected = options.find((option) => String(option.value) === String(value));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => option.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;
    setQuery("");
   
    const raf = requestAnimationFrame(() => searchRef.current?.focus());

    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const list = listRef.current;

    if (!list) return undefined;

    const handleTouchStart = (event) => {
      if (event.touches.length !== 1) {
        touchYRef.current = null;
        return;
      }

      touchYRef.current =
        event.touches[0].clientY;
    };

    const handleTouchMove = (event) => {
      if (
        event.touches.length !== 1 ||
        touchYRef.current === null
      ) {
        return;
      }

      const maxScroll =
        list.scrollHeight -
        list.clientHeight;

      if (maxScroll <= 0) {
        return;
      }

      const currentY =
        event.touches[0].clientY;

      const delta =
        touchYRef.current -
        currentY;

      touchYRef.current =
        currentY;

      if (!delta) {
        return;
      }

      const before =
        list.scrollTop;

      const target =
        Math.max(
          0,
          Math.min(
            maxScroll,
            before + delta,
          ),
        );

      if (target === before) {
        return;
      }

      list.scrollTop = target;

      event.preventDefault();
      event.stopPropagation();
    };

    const clearTouch = () => {
      touchYRef.current = null;
    };

    list.addEventListener(
      "touchstart",
      handleTouchStart,
      { passive: true },
    );

    list.addEventListener(
      "touchmove",
      handleTouchMove,
      { passive: false },
    );

    list.addEventListener(
      "touchend",
      clearTouch,
      { passive: true },
    );

    list.addEventListener(
      "touchcancel",
      clearTouch,
      { passive: true },
    );

    return () => {
      list.removeEventListener(
        "touchstart",
        handleTouchStart,
      );

      list.removeEventListener(
        "touchmove",
        handleTouchMove,
      );

      list.removeEventListener(
        "touchend",
        clearTouch,
      );

      list.removeEventListener(
        "touchcancel",
        clearTouch,
      );

      touchYRef.current = null;
    };
  }, [open]);

  return (
    <div className={`og-combo${open ? " is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        id={id}
        className="og-combo__button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((current) => !current)}
      >
        <span className={selected ? "og-combo__value" : "og-combo__placeholder"}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={16} className="og-combo__chevron" />
      </button>

      {open && (
        <div className="og-combo__panel" role="listbox">
          <div className="og-combo__search">
            <Search size={15} />
            <input
              ref={searchRef}
              type="text"
              value={query}
              placeholder="Search…"
              onChange={(event) => setQuery(event.target.value)}
              aria-label="Search options"
            />
          </div>
          <div
            ref={listRef}
            className="og-combo__list"
          >
            {!filtered.length ? (
              <div className="og-combo__empty">{emptyMessage}</div>
            ) : (
              filtered.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  role="option"
                  aria-selected={String(option.value) === String(value)}
                  className={`og-combo__option${
                    String(option.value) === String(value) ? " is-selected" : ""
                  }`}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AvailabilityBoard = ({
  venues = [],
  forcedVenueId = "",
  ensureGroundsForVenue,
  onOpenCustom,
  onChanged,
  onOpenList,
  refreshKey = 0,
}) => {
  const [venueId, setVenueId] = useState(() => forcedVenueId || getStoredVenueId());
  const [groundId, setGroundId] = useState("");
  const [grounds, setGrounds] = useState([]);
  const [selectedDate, setSelectedDate] = useState(ymd(new Date()));
  const [weekStart, setWeekStart] = useState(() => fromYmd(ymd(new Date())));
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busySlot, setBusySlot] = useState("");
  const [detail, setDetail] = useState(null);
  const [wholeDayConfirmOpen, setWholeDayConfirmOpen] = useState(false);
  const [weekStatuses, setWeekStatuses] = useState({});
  const bookingCloseRef = useRef(null);
  const bookingTriggerRef = useRef(null);
  const activeDetailKeyRef = useRef(null);
  const groundControlsRef = useRef(null);
  const groundTabsMeasureRef = useRef(null);
  const [groundsOverflow, setGroundsOverflow] = useState(false);

  useEffect(() => {
    if (forcedVenueId) {
      setVenueId(String(forcedVenueId));
      return;
    }
    if (!venues.length) return;

    const venueExists = (id) =>
      venues.some((venue) => String(venue._id || venue.id) === String(id));

    if (venueId && venueExists(venueId)) return;
    const stored = getStoredVenueId();
    const fallback = venueExists(stored)
      ? stored
      : String(venues[0]._id || venues[0].id || "");

    setVenueId(fallback);
  }, [forcedVenueId, venueId, venues]);

  useEffect(() => {
    if (forcedVenueId || !venueId) return;
    setStoredVenueId(venueId);
  }, [forcedVenueId, venueId]);

  useEffect(() => {
    let active = true;

    const loadGrounds = async () => {
      if (!venueId) {
        setGrounds([]);
        setGroundId("");
        return;
      }

      const list = await ensureGroundsForVenue?.(venueId);
      if (!active) return;
      const next = Array.isArray(list) ? list : [];
      setGrounds(next);
      setGroundId((current) => {
        const exists = next.some(
          (ground) => String(ground._id || ground.id) === String(current),
        );
        return exists ? current : String(next[0]?._id || next[0]?.id || "");
      });
    };

    loadGrounds();
    return () => {
      active = false;
    };
  }, [ensureGroundsForVenue, venueId]);

  const loadBoard = async ({ silent = false } = {}) => {
    if (!groundId || !selectedDate) {
      setBoard(null);
      return;
    }

    // Only show the full-grid loading state for a "real" load (first
    // load, switching ground/date). A silent refresh — e.g. right after
    // blocking/unblocking a slot — keeps the existing grid on screen and
    // swaps in the fresh data once it arrives, so the UI doesn't flash a
    // spinner over slots the admin can already see are up to date.
    if (!silent) setLoading(true);
    try {
      const response = await getAvailabilityBoard(groundId, {
        date: selectedDate,
      });
      setBoard(response);
    } catch (error) {
      if (!silent) setBoard(null);
      toast.error(
        error?.response?.data?.message || "Failed to load availability",
      );
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Tracks which ground/date the board was last loaded for, so we can
  // tell a "real" navigation (switching ground or date — show the full
  // loading state) apart from a post-action refresh (parent bumping
  // refreshKey after a block/unblock — load quietly in the background,
  // no API calls changed, just no spinner flash over data that's still
  // valid on screen).
  const boardIdentityRef = useRef({ groundId: null, selectedDate: null });

  useEffect(() => {
    const identityChanged =
      boardIdentityRef.current.groundId !== groundId ||
      boardIdentityRef.current.selectedDate !== selectedDate;
    boardIdentityRef.current = { groundId, selectedDate };
    loadBoard({ silent: !identityChanged });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groundId, selectedDate, refreshKey]);

  useEffect(() => {
  setDetail(null);
  activeDetailKeyRef.current = null;
  bookingTriggerRef.current = null;
}, [groundId, selectedDate]);

  useEffect(() => {
    if (!detail) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setDetail(null);
        activeDetailKeyRef.current = null;
        requestAnimationFrame(() => {
          bookingTriggerRef.current?.focus();
        });
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [detail]);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart],
  );

  // Switch the ground tabs to a dropdown whenever they don't all fit in the
  // available width — most commonly on mobile, but this also covers a
  // desktop window with many grounds. A hidden measurer (same buttons,
  // laid out at natural width) lets us detect overflow without needing the
  // real tabs to be visible.
  useEffect(() => {
    const measureEl = groundTabsMeasureRef.current;
    const containerEl = groundControlsRef.current;
    if (!measureEl || !containerEl) return undefined;

    const measure = () => {
      setGroundsOverflow(measureEl.scrollWidth > containerEl.clientWidth);
    };

    measure();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(containerEl);
    }
    window.addEventListener("resize", measure);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [grounds]);

  useEffect(() => {
    let active = true;

    const loadWeekStatuses = async () => {
      if (!groundId) {
        setWeekStatuses({});
        return;
      }

      const results = await Promise.all(
        weekDays.map(async (date) => {
          const dateValue = ymd(date);

          try {
            const response = await getAvailabilityBoard(groundId, { date: dateValue });
            const slots = Array.isArray(response?.slots) ? response.slots : [];
            const allBookedOrBlocked =
              slots.length > 0 &&
              slots.every(
                (slot) => slot.status === "booked" || slot.status === "blocked",
              );
            const hasBlockedSlot = slots.some((slot) => slot.status === "blocked");

            return [
              dateValue,
              allBookedOrBlocked ? "full" : hasBlockedSlot ? "partial-blocked" : "normal",
            ];
          } catch (_) {
            return [dateValue, "normal"];
          }
        }),
      );

      if (active) setWeekStatuses(Object.fromEntries(results));
    };

    loadWeekStatuses();

    return () => {
      active = false;
    };
  }, [groundId, weekDays, refreshKey]);

  const activeGround = grounds.find(
    (ground) => String(ground._id || ground.id) === String(groundId),
  );

  const todayYmd = ymd(new Date());
  const currentWeekStart = fromYmd(todayYmd);
  const isCurrentWeek = ymd(weekStart) === todayYmd;
  const weekEnd = addDays(weekStart, 6);

  const closeBookingDetail = () => {
    setDetail(null);
    activeDetailKeyRef.current = null;
    requestAnimationFrame(() => {
      bookingTriggerRef.current?.focus();
    });
  };

  const moveWeek = (amount) => {
    setWeekStart((current) => {
      const next = addDays(current, amount * 7);
      return next < currentWeekStart ? currentWeekStart : next;
    });
    setSelectedDate((current) => {
      const next = ymd(addDays(fromYmd(current), amount * 7));
      return next < todayYmd ? todayYmd : next;
    });
  };

  const refreshEverything = async () => {
    await loadBoard({ silent: true });
    await onChanged?.();
  };

  const isSameSlot = (a, b) =>
    !!a && !!b && a.startTime === b.startTime && a.endTime === b.endTime;

  // Applies a local, in-place status change to one slot immediately (before
  // the server confirms it), so the admin sees the result the instant they
  // act instead of waiting through a round trip. Returns the previous board
  // so the caller can roll back on failure.
  const applyOptimisticSlot = (slot, patch) => {
    let previousBoard = null;
    setBoard((current) => {
      if (!current) return current;
      previousBoard = current;
      return {
        ...current,
        slots: current.slots.map((candidate) =>
          isSameSlot(candidate, slot) ? { ...candidate, ...patch } : candidate,
        ),
      };
    });
    return previousBoard;
  };

  const handleSlot = async (slot, triggerElement = null) => {
  const slotKey = `${groundId}-${selectedDate}-${slot.startTime}-${slot.endTime}`;
    if (slot.status === "booked" || slot.status === "hold") {
     
      if (activeDetailKeyRef.current === slotKey) {
        closeBookingDetail();
        return;
      }
      activeDetailKeyRef.current = slotKey;
      bookingTriggerRef.current = triggerElement;
      setDetail({ slot, bookings: slot.bookings || [] });
      return;
    }

    if (slot.status === "blocked") {
      if (!slot.block?.canQuickUnblock) {
        toast.info("Use the blocked-slot list to manage this grouped or shared block.");
        return;
      }

      setBusySlot(slotKey);
      // Show it as available right away; roll back if the request fails.
      const previousBoard = applyOptimisticSlot(slot, {
        status: "available",
        block: null,
      });
      try {
        if (slot.block.sourceType === "recurring") {
          await createRecurringUnblockException(slot.block.id, {
            date: selectedDate,
            note: "",
          });
        } else {
          await unblockAvailability(slot.block.id);
        }
        toast.success("Slot unblocked");
        await refreshEverything();
      } catch (error) {
        if (previousBoard) setBoard(previousBoard);
        toast.error(error?.response?.data?.message || "Unable to unblock slot");
      } finally {
        setBusySlot("");
      }
      return;
    }

    setBusySlot(slotKey);
    // Show it as blocked right away; roll back if the request fails.
    const previousBoard = applyOptimisticSlot(slot, {
      status: "blocked",
      block: { canQuickUnblock: false, sourceType: "manual", note: "" },
    });
    try {
      await blockAvailability(groundId, {
        date: selectedDate,
        startTime: slot.startTime,
        endTime: slot.endTime,
        note: "",
      });
      toast.success("Slot blocked");
      await refreshEverything();
    } catch (error) {
      if (previousBoard) setBoard(previousBoard);
      toast.error(error?.response?.data?.message || "Unable to block slot");
    } finally {
      setBusySlot("");
    }
  };

  const confirmBlockWholeDay = async () => {
    if (!groundId) return;

    const availableSlots = (board?.slots || []).filter(
      (slot) => slot.status === "available",
    );

    if (!availableSlots.length) {
      toast.info("No available slots left to block for this day");
      setWholeDayConfirmOpen(false);
      return;
    }

    setBusySlot("whole-day");
    // Mark every currently-available slot as blocked right away instead of
    // waiting for every request to settle; any that fail get reverted
    // below once we know which ones those are.
    const optimisticSlotKeys = new Set(
      availableSlots.map((slot) => `${slot.startTime}-${slot.endTime}`),
    );
    const previousBoard = board;
    setBoard((current) => {
      if (!current) return current;
      return {
        ...current,
        slots: current.slots.map((slot) =>
          optimisticSlotKeys.has(`${slot.startTime}-${slot.endTime}`)
            ? { ...slot, status: "blocked", block: { canQuickUnblock: false, sourceType: "manual", note: "" } }
            : slot,
        ),
      };
    });
    try {
     
      const results = await Promise.allSettled(
        availableSlots.map((slot) =>
          blockAvailability(groundId, {
            date: selectedDate,
            startTime: slot.startTime,
            endTime: slot.endTime,
            note: "",
          }),
        ),
      );

      const failed = results
        .map((result, index) => ({ result, slot: availableSlots[index] }))
        .filter(({ result }) => result.status === "rejected");

      if (failed.length === 0) {
        toast.success("Available slots blocked for the day");
      } else if (failed.length < results.length) {
        // Revert just the slots that failed — the rest stay optimistically
        // blocked and are confirmed by the refresh below.
        const failedKeys = new Set(
          failed.map(({ slot }) => `${slot.startTime}-${slot.endTime}`),
        );
        setBoard((current) => {
          if (!current) return current;
          return {
            ...current,
            slots: current.slots.map((slot) =>
              failedKeys.has(`${slot.startTime}-${slot.endTime}`)
                ? { ...slot, status: "available", block: null }
                : slot,
            ),
          };
        });
        toast.warning(
          `Blocked ${results.length - failed.length} of ${results.length} slots. Some slots could not be blocked.`,
        );
      } else {
        setBoard(previousBoard);
        toast.error(
          failed[0]?.result?.reason?.response?.data?.message || "Unable to block whole day",
        );
      }

      setWholeDayConfirmOpen(false);
      await refreshEverything();
    } finally {
      setBusySlot("");
    }
  };

  return (
    <section className="og-availability" aria-label="Ground availability">
      <header className="og-availability__header">
        <div>
          <h1 className="sa-topbar-managed-page-title">Availability</h1>
          <p>Tap a slot to block it. Tap again to unblock it.</p>
        </div>
        <div className="og-availability__top-actions">
          <FormButton
            type="button"
            variant="primary"            className="og-button og-button--primary"
            disabled={!groundId}
            onClick={() => onOpenCustom?.({ venueId, groundId, date: selectedDate })}
          >
            <Ban size={15} />
            Block custom range
          </FormButton>
          <FormButton
            type="button"
            variant="outline"
            className="og-button og-button--outline"
            onClick={onOpenList}
          >
            <List size={15} />
            Blocked List
          </FormButton>
        </div>
      </header>

      <div className="og-availability__controls">
        <div className="og-availability__venue">
          <label htmlFor="og-venue-select">Venue</label>
          <SearchableCombobox
            id="og-venue-select"
            value={venueId}
            onChange={setVenueId}
            disabled={!!forcedVenueId || !venues.length}
            placeholder="Select a venue…"
            emptyMessage="No venues available"
            options={venues.map((venue) => ({
              value: String(venue._id || venue.id || ""),
              label: venue.name || venue.title || venue.slug || venue._id || venue.id,
            }))}
          />
        </div>

        <div className="og-availability__ground" ref={groundControlsRef}>
          <label htmlFor={groundsOverflow ? "og-ground-select" : undefined}>Ground</label>
          <div className="og-ground-tabs-measure" aria-hidden="true" ref={groundTabsMeasureRef}>
            {grounds.map((ground) => {
              const id = String(ground._id || ground.id);
              return <span key={id}>{ground.name || ground.title || id}</span>;
            })}
          </div>

          {groundsOverflow ? (
            <SearchableCombobox
              id="og-ground-select"
              value={groundId}
              onChange={setGroundId}
              disabled={!grounds.length}
              placeholder="Select a ground…"
              emptyMessage="No grounds available"
              options={grounds.map((ground) => ({
                value: String(ground._id || ground.id),
                label: ground.name || ground.title || String(ground._id || ground.id),
              }))}
            />
          ) : (
            <div className="og-ground-tabs" role="tablist" aria-label="Grounds">
              {grounds.map((ground) => {
                const id = String(ground._id || ground.id);
                return (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={id === groundId}
                    className={id === groundId ? "is-active" : ""}
                    key={id}
                    onClick={() => setGroundId(id)}
                  >
                    {ground.name || ground.title || id}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="og-week-card">
        <div className="og-week-header">
          <button
            type="button"
            className="og-week-nav"
            aria-label="Previous week"
            onClick={() => moveWeek(-1)}
            disabled={isCurrentWeek}
          >
            <ChevronLeft />
          </button>
          <div className="og-week-title">
            {formatDay(weekStart, { month: "short", day: "numeric" })} – {formatDay(weekEnd, { month: "short", day: "numeric" })}
          </div>
          <button type="button" className="og-week-nav" aria-label="Next week" onClick={() => moveWeek(1)}>
            <ChevronRight />
          </button>
        </div>
        <div className="og-week-days">
          {weekDays.map((date) => {
            const value = ymd(date);
            const selected = value === selectedDate;
            const isPast = value < todayYmd;
            const isToday = value === todayYmd;
            const dateStatus = weekStatuses[value] || "normal";
            const className = [
              selected ? "is-active" : "",
              dateStatus === "partial-blocked" ? "is-partial-blocked" : "",
              dateStatus === "full" ? "is-full" : "",
              isPast ? "is-past" : "",
              isToday ? "is-today" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <button
                type="button"
                key={value}
                className={className}
                aria-pressed={selected}
                aria-disabled={isPast}
                disabled={isPast}
                aria-label={`${isToday ? "Today, " : ""}${formatDay(date, {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}. ${
                  dateStatus === "full"
                    ? "All slots booked or blocked"
                    : dateStatus === "partial-blocked"
                      ? "Has blocked slots"
                      : "No blocked slots"
                }${selected ? ". Selected" : ""}`}
                onClick={() => {
                  if (isPast) return;
                  setSelectedDate(value);
                }}
              >
                <span>{formatDay(date, { weekday: "short" })}</span>
                <strong>
                  <b>{formatDay(date, { day: "numeric" })}</b>
                  <em className="og-week-month"> {formatDay(date, { month: "short" })}</em>
                </strong>
              </button>
            );
          })}
        </div>
      </div>

      <div className="og-slots-card">
        <div className="og-slots-card__header">
          <h2 className="og-slots-title">
            <span className="og-slots-title__ground">
              {activeGround?.name || board?.ground?.name || "Ground"}
            </span>
            <span className="og-slots-title__date">
              {formatDay(fromYmd(selectedDate), {
                weekday: "long",
                day: "numeric",
                month: "short",
              })}
            </span>
          </h2>
          <button
            type="button"
            className="og-button og-button--outline"
            onClick={() => setWholeDayConfirmOpen(true)}
            disabled={!groundId || busySlot === "whole-day"}
          >
            {busySlot === "whole-day" && <Loader2 className="og-spin" size={16} />}
            Block whole day
          </button>
        </div>

        {loading ? (
          <div className="og-board-state"><Loader2 className="og-spin" /> Loading availability…</div>
        ) : !groundId ? (
          <div className="og-board-state">Select a venue and ground.</div>
        ) : !(board?.slots || []).length ? (
          <div className="og-board-state">No bookable slots for this date.</div>
        ) : (
          <div className="og-slot-grid">
            {board.slots.map((slot) => {
              const key = `${slot.startTime}-${slot.endTime}`;
              const booking = slot.booking;
              const tooltip = booking
                ? `${booking.customerName} · ${booking.bookingId} · ${booking.bookingSource}`
                : slot.block?.note || STATUS_COPY[slot.status];
              return (
                <button
                  type="button"
                  key={key}
                  className={`og-slot og-slot--${slot.status}`}
                  aria-label={`${slotLabel(slot)}. ${tooltip}`}
                  data-tooltip={tooltip}
                  onClick={(event) => handleSlot(slot, event.currentTarget)}
                  disabled={busySlot === key}
                >
                  {busySlot === key ? <Loader2 className="og-spin" size={17} /> : slotLabel(slot)}
                </button>
              );
            })}
          </div>
        )}

        <div className="og-legend">
          <span><i className="is-available" />Available</span>
          <span><i className="is-blocked" />Blocked by venue</span>
          <span><i className="is-booked" />Booked</span>
          <span><i className="is-hold" />Payment hold</span>
        </div>
      </div>

      {!!detail?.bookings?.length && (
        <div
          className="og-booking-detail-layer"
          role="dialog"
          aria-labelledby="og-booking-dialog-title"
        >
          <div className="og-booking-card" aria-live="polite">
            <div className="og-booking-card__icon"><UserRound /></div>
            <div className="og-booking-card__customer">
              <strong id="og-booking-dialog-title">{detail.bookings[0].customerName}</strong>
              <span>{slotLabel(detail.slot)} · {detail.bookings[0].maskedPhone}</span>
              {detail.bookings[0].amount != null && (
                <span>{detail.bookings[0].currency} {Number(detail.bookings[0].amount).toLocaleString()}</span>
              )}
            </div>
            <div className="og-booking-card__meta">
              <span>Booked via</span>
              <strong>{detail.bookings[0].bookingSource}</strong>
            </div>
            <div className="og-booking-card__meta">
              <span>Booking ID</span>
              <strong>{detail.bookings[0].bookingId}</strong>
            </div>
            <button
              ref={bookingCloseRef}
              type="button"
              className="og-booking-card__close"
              aria-label="Close booking information"
              onClick={closeBookingDetail}
            >
              <X />
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={wholeDayConfirmOpen}
        title="Block Whole Day"
        message={`Block all available slots for ${
          activeGround?.name || board?.ground?.name || "this ground"
        } on ${formatDay(fromYmd(selectedDate), {
          weekday: "long",
          day: "numeric",
          month: "short",
          year: "numeric",
        })}?`}
        confirmText="Block whole day"
        cancelText="Cancel"
        loading={busySlot === "whole-day"}
        onConfirm={confirmBlockWholeDay}
        onCancel={() => {
          if (busySlot !== "whole-day") setWholeDayConfirmOpen(false);
        }}
      />
    </section>
  );
};

export default AvailabilityBoard;